import { useCallback, useEffect, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Actions, RootState } from "@store/index";
import {
  Attribute,
  Brand,
  Category,
  Color,
  Product,
  StoreType,
  Unit,
} from "@apis/index";

import useConfig from "@hooks/useConfig";

export const useProductForm = (data: any) => {
  const location = useLocation();
  const { bucketUrl } = useConfig();
  const { data: user } = useSelector((state: RootState) => state.user);

  const { productId } = useParams();
  const [productData, setProductData] = useState<any>(
    data?.productData || location.state?.productData || null,
  );

  const fetchProduct = useCallback(() => {
    const id = productData?.id || productId;
    if (id) {
      Product.find({ id }).then((res: any) => {
        if (res) {
          setProductData(res);
        }
      });
    }
  }, [productData?.id, productId]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const moduleId =
    user?.role === "store"
      ? user?.module?.id
      : data?.moduleId ||
        data?.module?.id ||
        productData?.moduleId ||
        productData?.module?.id ||
        productData?.store?.moduleId ||
        productData?.store?.module?.id;

  const storeId =
    user?.role === "store"
      ? user?.id
      : data?.id || data?.id || productData?.storeId || productData?.store?.id;

  const isPureVeg =
    user?.role === "store"
      ? user?.isPureVeg
      : data?.isPureVeg ||
        productData?.store?.isPureVeg ||
        data?.store?.isPureVeg;

  const [selectedColors, setSelectedColors] = useState<any[]>([]);
  const [selectedAttributes, setSelectedAttributes] = useState<any[]>([]);
  const [variantError, setVariantError] = useState<string>("");
  const [isInitialized, setIsInitialized] = useState(false);
  const dispatch = useDispatch();

  // Selectors
  const { items: attributes } = useSelector(
    (state: RootState) => state.attributes,
  );
  const { data: storeTypes } = useSelector(
    (state: RootState) => state.storeTypes,
  );
  const { items: colors } = useSelector((state: RootState) => state.colors);
  const { items: categories } = useSelector(
    (state: RootState) => state.categories,
  );
  const { items: subcategories } = useSelector(
    (state: RootState) => state.subcategories,
  );
  const { items: brands } = useSelector((state: RootState) => state.brands);
  const { data: units } = useSelector((state: RootState) => state.units);

  // Reconstruct selected colors and attributes from product variations once productData and master lists are loaded
  useEffect(() => {
    if (isInitialized || !productData || !attributes || attributes.length === 0) return;

    if (productData.variations && productData.variations.length > 0) {
      // 1. Reconstruct selectedColors
      const uniqueColorsMap = new Map();
      productData.variations.forEach((v: any) => {
        if (v.color && v.color.id) {
          uniqueColorsMap.set(v.color.id, v.color);
        }
      });
      setSelectedColors(Array.from(uniqueColorsMap.values()));

      // 2. Reconstruct selectedAttributes
      const attrValuesMap = new Map<string, Set<string>>();
      productData.variations.forEach((v: any) => {
        if (v.attributes) {
          Object.keys(v.attributes).forEach((key) => {
            const rawVal = v.attributes[key];
            if (rawVal) {
              const trimmedKey = key.trim();
              const trimmedVal = typeof rawVal === "string" ? rawVal.trim() : rawVal;
              if (!attrValuesMap.has(trimmedKey)) {
                attrValuesMap.set(trimmedKey, new Set());
              }
              attrValuesMap.get(trimmedKey)!.add(trimmedVal);
            }
          });
        }
      });

      if (attrValuesMap.size > 0) {
        const reconstructedAttrs: any[] = [];
        attrValuesMap.forEach((valSet, attrName) => {
          const masterAttr = attributes.find(
            (a: any) => a.name.toLowerCase() === attrName.toLowerCase()
          );
          const selectedVals = Array.from(valSet);
          if (masterAttr) {
            const allValues = Array.from(new Set([...(masterAttr.values || []), ...selectedVals]));
            reconstructedAttrs.push({
              ...masterAttr,
              values: allValues,
              selectedValues: selectedVals,
            });
          } else {
            reconstructedAttrs.push({
              id: `temp-${attrName}`,
              name: attrName,
              values: selectedVals,
              selectedValues: selectedVals,
            });
          }
        });
        setSelectedAttributes(reconstructedAttrs);
      }
    }
    setIsInitialized(true);
  }, [productData, attributes, isInitialized]);

  const fetchStoreTypes = useCallback(async () => {
    await StoreType.list({
      state: "storeTypes",
      params: { moduleId, limit: 3000 },
    });
  }, [moduleId]);

  const fetchAttributes = useCallback(async () => {
    await Attribute.list({
      state: "attributes",
      params: { moduleId, limit: 3000 },
    });
  }, [moduleId]);

  const fetchColors = useCallback(async () => {
    await Color.list({ state: "colors", params: { moduleId, limit: 3000 } });
  }, [moduleId]);

  const fetchCategories = useCallback(async () => {
    await Category.list({
      state: "categories",
      params: { parentId: 0, moduleId, limit: 3000 },
    });
  }, [moduleId]);

  const fetchSubCategories = useCallback(
    async (parentId: string) => {
      await Category.list({
        state: "subcategories",
        params: { parentId, moduleId, limit: 3000 },
      });
    },
    [moduleId],
  );

  const fetchBrands = useCallback(async () => {
    await Brand.list({ state: "brands", params: { moduleId, limit: 3000 } });
  }, [moduleId]);

  const fetchUnits = useCallback(async () => {
    await Unit.list({ state: "units", params: { moduleId, limit: 3000 } });
  }, [moduleId]);

  useEffect(() => {
    if (productData?.lensAddons) {
      const formattedLensAddons = Array.isArray(productData.lensAddons)
        ? productData.lensAddons
        : [];

      dispatch(Actions.set("lensaddons", formattedLensAddons));
    } else {
      dispatch(Actions.set("lensaddons", []));
    }

    return () => {
      dispatch(Actions.set("lensaddons", []));
    };
  }, [productData, dispatch]);

  const fetchAll = useCallback(async () => {
    if (!moduleId) return;
    await Promise.all([
      fetchAttributes(),
      fetchColors(),
      fetchCategories(),
      fetchBrands(),
      fetchUnits(),
      fetchStoreTypes(),
    ]);
  }, [
    moduleId,
    fetchAttributes,
    fetchColors,
    fetchCategories,
    fetchBrands,
    fetchUnits,
    fetchStoreTypes,
  ]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const fetchSingleAttribute = useCallback(async (id: string) => {
    await Attribute.find({ state: "singleAttribute", id }).then((res) => {
      setSelectedAttributes((prev) => [...prev, res]);
    });
  }, []);

  const fetchSingleColor = useCallback(async (id: string) => {
    await Color.find({ state: "singleColor", id }).then((res) => {
      setSelectedColors((prev) => [...prev, res]);
    });
  }, []);

  // Options formatting
  const brandOptions = brands.map((brand) => ({
    id: brand.id,
    name: brand.name,
    image: `${bucketUrl}/${brand.image}`,
  }));

  const categoryOptions = categories.map((cat) => ({
    id: cat.id,
    name: cat.name,
    image: `${bucketUrl}/${cat.icon}`,
  }));

  const subCategoryOptions = subcategories.map((sub) => ({
    id: sub.id,
    name: sub.name,
    image: `${bucketUrl}/${sub.icon}`,
  }));
  const storeTypeOptions = storeTypes?.map((storeType) => ({
    id: storeType.id,
    name: storeType.name,
  }));
  return {
    productData,
    user,
    selectedColors,
    selectedAttributes,
    variantError,
    setVariantError,
    setSelectedColors,
    setSelectedAttributes,
    fetchSingleAttribute,
    fetchSingleColor,
    fetchSubCategories,
    attributes,
    colors,
    categories,
    subcategories,
    brands,
    units,
    brandOptions,
    categoryOptions,
    subCategoryOptions,
    moduleId,
    storeId,
    isPureVeg,
    fetchProduct,
  };
};
